import {
  CLASSIC_COLUMN_COUNT,
  CLASSIC_MULTIPLIER_FACTOR,
  CLASSIC_PRICE_STEP_USD,
  CLASSIC_ROW_COUNT,
  type ClassicTile,
  type MarketDirection,
  type MarketTick,
  type PriceBand,
  type Rarity,
  type SpeciesDefinition,
} from './contracts'
import { SPECIES } from './species'

const BAND_MULTIPLIERS = [12, 5, 2.5, 1.4, 2.5, 5, 12]
const CLASSIC_BASE_MULTIPLIER = 1.3675
const CLASSIC_ODDS_GROWTH = 0.03
const CLASSIC_COLUMN_INTERVAL_MS = 5_000
const CLASSIC_FIRST_RESOLVE_MS = 10_000
const CLASSIC_MAX_MULTIPLIER = 125

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

export function realizedVolatility(ticks: MarketTick[], fallbackPrice: number): number {
  const recent = ticks.slice(-240)
  if (recent.length < 4) return fallbackPrice * 0.00035

  const returns: number[] = []
  for (let index = 1; index < recent.length; index += 1) {
    const previous = recent[index - 1].price
    const current = recent[index].price
    if (previous > 0) returns.push(Math.log(current / previous))
  }

  const mean = returns.reduce((sum, value) => sum + value, 0) / returns.length
  const variance = returns.reduce((sum, value) => sum + (value - mean) ** 2, 0) / returns.length
  const standardDeviation = Math.sqrt(variance)

  return clamp(fallbackPrice * standardDeviation * 9, fallbackPrice * 0.00025, fallbackPrice * 0.0015)
}

export function dynamicClassicPriceStep(
  ticks: MarketTick[],
  fallbackPrice: number,
  configuredMaxStep = CLASSIC_PRICE_STEP_USD,
  options: { targetRows?: number; minStep?: number } = {},
): number {
  const targetRows = Math.max(4, options.targetRows ?? 6)
  const minStep = Math.max(1, options.minStep ?? configuredMaxStep * 0.5)
  const maxStep = Math.max(minStep, configuredMaxStep)
  const latestTimestamp = ticks.at(-1)?.sourceTimestamp
  const recent = latestTimestamp
    ? ticks.filter((tick) => tick.sourceTimestamp >= latestTimestamp - 5 * 60_000)
    : ticks
  if (recent.length < 8) return maxStep

  const prices = recent.map((tick) => tick.price).filter((price) => Number.isFinite(price) && price > 0)
  if (prices.length < 8) return maxStep
  const range = Math.max(...prices) - Math.min(...prices)
  const rangeStep = range / targetRows
  const volatilityStep = realizedVolatility(recent, fallbackPrice) / 8
  const rawStep = Math.min(maxStep, Math.max(rangeStep, volatilityStep, minStep))
  return Number(clamp(rawStep, minStep, maxStep).toFixed(2))
}

export function directionForBand(index: number, centerIndex: number): MarketDirection {
  if (index < centerIndex) return 'up'
  if (index > centerIndex) return 'down'
  return 'range'
}

export function buildPriceBands(entryPrice: number, bandSize: number): PriceBand[] {
  const centerIndex = 3
  return BAND_MULTIPLIERS.map((multiplier, index) => {
    const offset = centerIndex - index
    const center = entryPrice + offset * bandSize
    return {
      index,
      min: center - bandSize / 2,
      max: center + bandSize / 2,
      center,
      multiplier,
      direction: directionForBand(index, centerIndex),
    }
  })
}

export function buildClassicTiles(
  roundId: string,
  entryPrice: number,
  priceStep: number,
  startedAt: number,
  options: { columns?: number; rows?: number; multiplierFactor?: number; quotedAt?: number } = {},
): ClassicTile[] {
  const columns = options.columns ?? CLASSIC_COLUMN_COUNT
  const rows = options.rows ?? CLASSIC_ROW_COUNT
  const multiplierFactor = options.multiplierFactor ?? CLASSIC_MULTIPLIER_FACTOR
  const quotedAt = options.quotedAt ?? startedAt
  const safePriceStep = priceStep > 0 ? priceStep : CLASSIC_PRICE_STEP_USD
  const priceAnchor = Math.round(entryPrice / safePriceStep) * safePriceStep
  const centerIndex = Math.floor(rows / 2)
  const tiles: ClassicTile[] = []

  for (let column = 0; column < columns; column += 1) {
    const secondsAhead = 10 + column * 5
    for (let index = 0; index < rows; index += 1) {
      const center = priceAnchor + (centerIndex - index) * safePriceStep
      const resolveAt = startedAt + secondsAhead * 1000
      tiles.push({
        id: `${roundId}:${column}:${index}`,
        column,
        index,
        min: center - safePriceStep / 2,
        max: center + safePriceStep / 2,
        center,
        multiplier: quoteClassicMultiplier(index, rows, resolveAt, quotedAt, multiplierFactor),
        direction: directionForBand(index, centerIndex),
        resolveAt,
        secondsAhead,
      })
    }
  }

  return tiles
}

export function classicMultiplier(distanceRows: number, timeSteps: number, multiplierFactor = CLASSIC_MULTIPLIER_FACTOR): number {
  const quoted = clamp(
    CLASSIC_BASE_MULTIPLIER * Math.exp(CLASSIC_ODDS_GROWTH * (Math.max(0, timeSteps) + Math.max(0, distanceRows) ** 2)),
    1.01,
    CLASSIC_MAX_MULTIPLIER,
  )
  return Number(clamp(quoted * multiplierFactor, 1.01, CLASSIC_MAX_MULTIPLIER).toFixed(2))
}

export function quoteClassicMultiplier(
  rowIndex: number,
  rows: number,
  resolveAt: number,
  quotedAt: number,
  multiplierFactor = CLASSIC_MULTIPLIER_FACTOR,
): number {
  const centerIndex = Math.floor(rows / 2)
  const distanceRows = Math.abs(rowIndex - centerIndex)
  const remainingAfterFirstColumn = resolveAt - quotedAt - CLASSIC_FIRST_RESOLVE_MS
  const timeSteps = Math.max(0, Math.round(remainingAfterFirstColumn / CLASSIC_COLUMN_INTERVAL_MS))
  return classicMultiplier(distanceRows, timeSteps, multiplierFactor)
}

export function quoteClassicTileMultiplier(tile: ClassicTile, quotedAt: number, multiplierFactor = CLASSIC_MULTIPLIER_FACTOR): number {
  return quoteClassicMultiplier(tile.index, CLASSIC_ROW_COUNT, tile.resolveAt, quotedAt, multiplierFactor)
}

export function findWinningBand(bands: PriceBand[], price: number): PriceBand | undefined {
  return bands.find((band, index) => {
    const isLast = index === bands.length - 1
    return price >= band.min && (price < band.max || (isLast && price <= band.max))
  })
}

export function priceDirection(entryPrice: number, resolvePrice: number, rangeThreshold: number): MarketDirection {
  const delta = resolvePrice - entryPrice
  if (Math.abs(delta) <= rangeThreshold) return 'range'
  return delta > 0 ? 'up' : 'down'
}

export function scaleRaidHealth(participants: number): number {
  return Math.max(240, Math.max(1, participants) * 240)
}

export function hashSeed(value: string): number {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

export function seededRandom(seed: number): () => number {
  let state = seed >>> 0
  return () => {
    state += 0x6d2b79f5
    let result = state
    result = Math.imul(result ^ (result >>> 15), result | 1)
    result ^= result + Math.imul(result ^ (result >>> 7), result | 61)
    return ((result ^ (result >>> 14)) >>> 0) / 4294967296
  }
}

function rarityPool(rarity: Rarity, includeBoss = false): SpeciesDefinition[] {
  return SPECIES.filter((species) => species.rarity === rarity && (includeBoss || !species.boss))
}

export function rollLoot(
  seedKey: string,
  options: { success: boolean; streak: number; multiplier: number; forceEpic?: boolean; raidBoss?: boolean },
): SpeciesDefinition | undefined {
  if (!options.success) return undefined
  if (options.raidBoss) return SPECIES.find((species) => species.boss)

  const random = seededRandom(hashSeed(seedKey))
  const roll = random()
  let rarity: Rarity

  if (options.forceEpic || options.streak >= 5) rarity = 'epic'
  else if (options.streak >= 3 && roll > 0.88) rarity = 'legendary'
  else if (options.multiplier >= 8 && roll > 0.68) rarity = 'epic'
  else if (roll > 0.72) rarity = 'rare'
  else rarity = 'common'

  const pool = rarityPool(rarity)
  return pool[Math.floor(random() * pool.length)] ?? rarityPool('common')[0]
}
